/**
 * The theme choice.
 *
 * PRD 04 §2: *one theme, hidden* — everything renders in `handcrafted-cutpaper`, and there
 * is no picker for the story world's look. Light vs. dark is a different axis entirely: it
 * is the reader's ground, not a style choice, so it does get a control (in the account
 * menu) and it defaults to `system`.
 *
 * `system` writes no attribute, so `prefers-color-scheme` in `tokens.css` decides; an
 * explicit choice stamps `data-theme` on `<html>` and wins in both directions.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';

export type ThemeChoice = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'atlas-nook-studio:theme';

interface ThemeValue {
  theme: ThemeChoice;
  setTheme: (theme: ThemeChoice) => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

function isChoice(value: unknown): value is ThemeChoice {
  return value === 'system' || value === 'light' || value === 'dark';
}

function readStored(): ThemeChoice {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isChoice(stored)) return stored;
  } catch {
    // A blocked storage falls back to the system setting, which is the right default anyway.
  }
  return 'system';
}

export function ThemeProvider({
  children,
  initialTheme,
}: {
  children: ReactNode;
  initialTheme?: ThemeChoice;
}): JSX.Element {
  const [theme, setThemeState] = useState<ThemeChoice>(() => initialTheme ?? readStored());

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
  }, [theme]);

  const setTheme = useCallback((next: ThemeChoice) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Ditto.
    }
  }, []);

  const value = useMemo<ThemeValue>(() => ({ theme, setTheme }), [theme, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const value = useContext(ThemeContext);
  if (value === null) throw new Error('useTheme: no ThemeProvider above this component');
  return value;
}
